import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PRODUCTS } from '../config/columns.config';
import { calculateAccountTotals } from '../utils/calculations';
import { formatMoney } from '../utils/formatters';
import { useTheme, alpha } from '@mui/material/styles';

const ReportTableBody = ({ data, config, expandedAccounts, onToggleAccount }) => {
  const theme = useTheme();

  const calculateProRata = (achieved, budget) => {
    if (!budget || !config?.dateRange?.start || !config?.dateRange?.end) {
      return 0;
    }

    const startDate = new Date(config.dateRange.start);
    const endDate = new Date(config.dateRange.end);
    const today = new Date();

    const totalDuration = endDate - startDate;
    if (totalDuration <= 0) return 0;

    let timeElapsed = 0;
    if (today < startDate) {
      timeElapsed = 0;
    } else if (today > endDate) {
      timeElapsed = totalDuration;
    } else {
      timeElapsed = today - startDate;
    }

    const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24)) + 1;
    const daysPassed = Math.ceil(timeElapsed / (1000 * 60 * 60 * 24)) + 1;

    const proRataBudget = (budget / totalDays) * daysPassed;

    return proRataBudget > 0 ? (achieved / proRataBudget) * 100 : 0;
  };

  const renderAccount = (account, level = 0) => {
    const isMainAccount = level === 0;
    const isExpanded = isMainAccount || expandedAccounts.has(account.id);
    const showDetails = config.reportType === 'sales_report_with_details' || config.reportType === 'purchase_report_with_details';
    const accountTotals = calculateAccountTotals(account, config) || {};
    const retailMap = accountTotals.retail || {};
    const retailOnlyMap = accountTotals.retailOnly || {};
    const showroomMap = accountTotals.showroomOnly || {};
    const projectMap = accountTotals.project || {};

    let rowBudget = config.budgetType === 'internal' ? (account.internalBudget || 0) : (account.externalBudget || 0);
    let rowRetailBudget = account.retailBudget || 0;
    let rowProjectBudget = account.projectBudget || 0;

    if (account.subAccounts && account.subAccounts.length > 0) {
      account.subAccounts.forEach(sub => {
        const subBud = config.budgetType === 'internal' ? (sub.internalBudget || 0) : (sub.externalBudget || 0);
        rowBudget += subBud;
        rowRetailBudget += (sub.retailBudget || 0);
        rowProjectBudget += (sub.projectBudget || 0);
      });
    }

    const showSubAccounts = (config.printMainAccountOnly === 'no') && account.subAccounts && account.subAccounts.length > 0;

    const visibleProjects = (account.projects || []).filter(p => !p.isHidden);
    const isAggregator = showSubAccounts;
    const hasRawProjects = !isAggregator && visibleProjects.length > 0;

    const showToggleButton = !isMainAccount && showDetails;

    const isSales = config.reportCategory === 'sales';

    const percentageAchieved = rowBudget ? (accountTotals.grandTotal / rowBudget) * 100 : 0;
    const proRataAchieved = calculateProRata(accountTotals.grandTotal, rowBudget);

    const indent = level * 20;

    const mainRowClass = "border-b font-bold";
    const subRowClass = "border-b";

    const totalSectionClass = "border-b font-bold";

    return (
      <React.Fragment key={account.id}>
        <tr
          className={isMainAccount ? mainRowClass : subRowClass}
          style={isMainAccount ? {
            backgroundColor: alpha(theme.palette.secondary.main, 0.25),
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary
          } : {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary
          }}
        >
          <td className="p-4 font-medium truncate" style={{ paddingLeft: `${16 + indent}px` }}>
            {showToggleButton && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAccount(account.id);
                }}
                className="inline-block mr-2"
                style={{ color: theme.palette.secondary.main, background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                {expandedAccounts.has(account.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
            <span style={{ color: isMainAccount ? theme.palette.text.primary : theme.palette.text.secondary }}>
              {account.id} - {account.name}
            </span>
          </td>

          {PRODUCTS.map(p => {
            const productTotal = (retailMap[p.key] || 0) + (projectMap[p.key] || 0);
            return (
              <td
                key={p.key}
                className="p-4 text-right truncate"
                style={{ color: isMainAccount ? theme.palette.text.primary : theme.palette.text.secondary }}
              >
                {formatMoney(productTotal)}
              </td>
            );
          })}

          <td className="p-4 text-right font-bold truncate" style={{ color: theme.palette.text.primary }}>
            {formatMoney(accountTotals.grandTotal)}
          </td>

          {isSales && (
            <>
              <td className="p-4 text-right truncate" style={{ color: isMainAccount ? theme.palette.text.primary : theme.palette.text.secondary }}>
                {formatMoney(rowBudget)}
              </td>
              <td className="p-4 text-right truncate" style={{ color: isMainAccount ? theme.palette.text.primary : theme.palette.text.secondary }}>
                {percentageAchieved.toFixed(2)} %
              </td>
              <td className="p-4 text-right truncate" style={{ color: isMainAccount ? theme.palette.text.primary : theme.palette.text.secondary }}>
                {proRataAchieved.toFixed(2)} %
              </td>
            </>
          )}
        </tr>

        {showDetails && isExpanded && (
          <>
            <tr
              className={totalSectionClass}
              style={{
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary
              }}
            >
              <td className="p-4 truncate italic pl-14" style={{ paddingLeft: `${56 + indent}px` }}>
                Total Retail
              </td>
              {PRODUCTS.map(p => (
                <td key={p.key} className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                  {formatMoney(retailMap[p.key] || 0)}
                </td>
              ))}
              <td className="p-4 text-right font-bold truncate" style={{ color: theme.palette.text.primary }}>
                {formatMoney(accountTotals.retailTotal)}
              </td>

              {isSales && (
                <>
                  <td className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                    {formatMoney(rowRetailBudget)}
                  </td>
                  <td className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                    {rowRetailBudget ? ((accountTotals.retailTotal / rowRetailBudget) * 100).toFixed(2) : '0.00'} %
                  </td>
                  <td className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                    {calculateProRata(accountTotals.retailTotal, rowRetailBudget).toFixed(2)} %
                  </td>
                </>
              )}
            </tr>

            <tr className="border-b" style={{ backgroundColor: alpha(theme.palette.background.default, 0.4), borderColor: theme.palette.divider }}>
              <td className="p-4 text-xs truncate" style={{ paddingLeft: `${76 + indent}px`, color: theme.palette.text.disabled }}>
                Retail
              </td>
              {PRODUCTS.map(p => (
                <td key={p.key} className="p-4 text-right text-xs truncate" style={{ color: theme.palette.text.disabled }}>
                  {formatMoney(retailOnlyMap[p.key] || 0)}
                </td>
              ))}
              <td className="p-4 text-right text-xs truncate" style={{ color: theme.palette.text.disabled }}>
                {formatMoney(accountTotals.retailOnlyTotal)}
              </td>

              {isSales && <><td className="p-4"></td><td className="p-4"></td><td className="p-4"></td></>}
            </tr>

            <tr className="border-b" style={{ backgroundColor: alpha(theme.palette.background.default, 0.4), borderColor: theme.palette.divider }}>
              <td className="p-4 text-xs truncate" style={{ paddingLeft: `${76 + indent}px`, color: theme.palette.text.disabled }}>
                Showroom
              </td>
              {PRODUCTS.map(p => (
                <td key={p.key} className="p-4 text-right text-xs truncate" style={{ color: theme.palette.text.disabled }}>
                  {formatMoney(showroomMap[p.key] || 0)}
                </td>
              ))}
              <td className="p-4 text-right text-xs truncate" style={{ color: theme.palette.text.disabled }}>
                {formatMoney(accountTotals.showroomOnlyTotal)}
              </td>

              {isSales && <><td className="p-4"></td><td className="p-4"></td><td className="p-4"></td></>}
            </tr>

            <tr
              className={totalSectionClass}
              style={{
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary
              }}
            >
              <td className="p-4 truncate italic" style={{ paddingLeft: `${56 + indent}px` }}>
                Total Project
              </td>
              {PRODUCTS.map(p => (
                <td key={p.key} className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                  {formatMoney(projectMap[p.key] || 0)}
                </td>
              ))}
              <td className="p-4 text-right font-bold truncate" style={{ color: theme.palette.text.primary }}>
                {formatMoney(accountTotals.projectTotal)}
              </td>

              {isSales && (
                <>
                  <td className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                    {formatMoney(rowProjectBudget)}
                  </td>
                  <td className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                    {rowProjectBudget ? ((accountTotals.projectTotal / rowProjectBudget) * 100).toFixed(2) : '0.00'} %
                  </td>
                  <td className="p-4 text-right truncate" style={{ color: theme.palette.text.primary }}>
                    {calculateProRata(accountTotals.projectTotal, rowProjectBudget).toFixed(2)} %
                  </td>
                </>
              )}
            </tr>

            {hasRawProjects && visibleProjects.map((project, idx) => {
              const projectTotal = project.total !== undefined
                ? project.total
                : PRODUCTS.reduce((sum, p) => sum + (project[p.key] || 0), 0);

              return (
                <tr key={`${project.id}-${idx}`} className="border-b" style={{ backgroundColor: alpha(theme.palette.background.default, 0.4), borderColor: theme.palette.divider }}>
                  <td className="p-4 text-xs truncate" style={{ paddingLeft: `${76 + indent}px`, color: theme.palette.text.secondary }}>
                    {project.id === 'GEN-PRJ' ? 'Projects' : `${project.id} - ${project.name}`}
                  </td>
                  {PRODUCTS.map(p => (
                    <td key={p.key} className="p-4 text-right text-xs truncate" style={{ color: theme.palette.text.secondary }}>
                      {formatMoney(project[p.key] || 0)}
                    </td>
                  ))}
                  <td className="p-4 text-right text-xs truncate" style={{ color: theme.palette.text.secondary }}>
                    {formatMoney(projectTotal)}
                  </td>

                  {isSales && <><td className="p-4"></td><td className="p-4"></td><td className="p-4"></td></>}
                </tr>
              );
            })}

            {showSubAccounts && account.subAccounts.map(sub => renderAccount(sub, level + 1))}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <tbody>
      {data.map(account => renderAccount(account))}
    </tbody>
  );
};

export default ReportTableBody;